import type Link from 'next/link';
import type { ComponentProps } from 'react';
import type { BrowsableFile } from '../../../lib/file-browser';
import { fileDurationLabel } from '../../../lib/file-browser';

/** One row of the Files list, resolved by the surface that owns the data.
 *
 *  The two Files surfaces differ in exactly these fields and nowhere else — where a row LINKS to, what it is
 *  keyed by, and whether it names the folder it came from — so this is the whole seam between them. Everything
 *  below it (the table, the grid, the actions, the sorting) is shared. */
export interface FilesRow {
  /** Stable across renders and unique within the list. */
  key: string;
  item: BrowsableFile;
  /** Where the row navigates on a modified click (cmd/middle) or when JS is not driving. */
  href: ComponentProps<typeof Link>['href'];
  as: string;
  /** The active mount prefix, for the file-type icon asset. */
  basePath: string;
  /** The folder this file sits in, spelled for a reader — set only while the list is FLAT (a search or filter
   *  spans the subtree), because inside a folder the breadcrumb already says where you are. */
  caption?: string;
  /** This file's own page as a new-tab URL, when the surface can name one. Feeds Share and the actions. */
  fileHref?: string;
  /** The owning workspace's display name, for the viewer's subtitle. */
  workspaceName?: string;
  /** A file still listed that can no longer be opened (a group file its owner stopped sharing, or deleted): drawn
   *  faded, as both phones' flat rows do. Absent everywhere else. */
  dimmed?: boolean;
  /** How long an audio or video file plays, when the surface KNOWS it without downloading the file. Only a
   *  chat attachment does today (`durationMs`, stamped when the voice note was recorded); a workspace file
   *  carries no duration, so its row simply shows none. */
  durationMs?: number;
}

/** The row's play length, or null — see `fileDurationLabel`. The list and the grid read this one answer. */
export function durationLabelOf(row: Pick<FilesRow, 'durationMs'>): string | null {
  return fileDurationLabel(row.durationMs);
}
